import { useTranslation } from 'react-i18next';
import { byDay } from '@d-orca/shared/client/notifications';
import { Menu } from '@components/ui/Menu/Menu';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { BellIcon } from '@utils/icons';
import type { INotificationItem } from '@interfaces/INotificationItem';
import { NotificationRow } from '../NotificationRow/NotificationRow';
import styles from './BellMenu.module.css';

export interface IBellMenuProps {
  count: number; // The number on the bell
  items: INotificationItem[]; // Newest first
  tz?: string; // The site's zone: with it, the rows fall under Today, Yesterday and Earlier
  onOpenItem: (item: INotificationItem) => void;

  // Told when the menu opens and closes, for whoever supplies what only an open menu needs.
  onOpenChange: (open: boolean) => void;

  // Swing animation
  swinging: boolean;
  onSwingEnd: () => void;
}

/** Capped so a long list can't widen the badge past its place. */
function countLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * The header bell and the menu behind it: a row for each notification.
 * Given its items already worded and drawn, and told nothing about which
 * feature raised them.
 */
export function BellMenu({ count, items, tz, onOpenItem, onOpenChange, swinging, onSwingEnd }: IBellMenuProps) {
  const { t } = useTranslation();
  const row = (item: INotificationItem) => <NotificationRow key={item.id} item={item} onOpen={() => onOpenItem(item)} />;

  return (
    <Menu.Root onOpenChange={onOpenChange}>
      <Menu.Trigger className={styles.bell} aria-label={t('notifications.bellLabel', { count })}>
        <span className={styles.bellIcon}>
          <span className={`${styles.glyph} ${swinging ? styles.swinging : ''}`} onAnimationEnd={onSwingEnd}>
            <BellIcon size={18} />
          </span>
          {count > 0 ? <span className={styles.badge}>{countLabel(count)}</span> : null}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="end" sideOffset={8}>
          <Menu.Popup className={styles.popup}>
            {items.length === 0 ? (
              <div className={styles.empty}>{t('notifications.empty')}</div>
            ) : (
              <ScrollArea className={styles.items}>
                {tz
                  ? byDay(items, tz).map(({ day, items: rows }) => (
                      <div key={day} role="group" aria-label={t(`notifications.day.${day}`)}>
                        <div className={styles.day}>{t(`notifications.day.${day}`)}</div>
                        {rows.map(row)}
                      </div>
                    ))
                  : items.map(row)}
              </ScrollArea>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
